import { useSession } from '@/features/auth/api/auth.api';
import { AccessLevel, type Grants, type PermissionSubject } from '@/types/domain';

/**
 * What the signed-in person may reach.
 *
 * The server resolves this and sends it with the session; the client uses it
 * to decide what to *offer*. Hiding a control nobody may use is manners - it
 * spares somebody filling in a form that will be refused - and it is never the
 * enforcement. Every one of these subjects is checked again on the API, which
 * is the only place it counts.
 */

const RANK: Record<AccessLevel, number> = {
  [AccessLevel.NONE]: 0,
  [AccessLevel.READ]: 1,
  [AccessLevel.WRITE]: 2,
};

/** Whether these grants reach the level this subject needs. Write implies read. */
export function can(
  grants: Grants | undefined,
  subject: PermissionSubject,
  level: AccessLevel = AccessLevel.READ,
): boolean {
  return RANK[grants?.[subject] ?? AccessLevel.NONE] >= RANK[level];
}

/**
 * The hook form, for a component deciding whether to render something.
 *
 * Returns false while the session is still loading, so a control never flashes
 * into view and then disappears once the answer arrives.
 */
export function useCan(subject: PermissionSubject, level: AccessLevel = AccessLevel.READ): boolean {
  const { data: user } = useSession();

  return can(user?.permissions, subject, level);
}
